import React, { useEffect, useState } from 'react'
import { FaDownload } from 'react-icons/fa'
import { Stack, Heading, Text, Box, EmptyCard, Alert, Card, Button, Icon, SimpleTable } from '@/components/lib'
import api from '../../../api'
import TrialLocker from '../../includes/TrialLocker'
import DatabaseLocker from '../../includes/DatabaseLocker'
import useStore from '../../../store'
import utils from '@/utils/utils'

export default function Locks () {
  const { user, trial, updateTrial } = useStore()
  const canManage = utils.canManageTrial(user, trial)
  // The study record taken at each database lock — the locked dataset,
  // with its checksums — listed with a download link. Built in the
  // background when the lock is applied, so it appears a minute or so later.
  const [lockedDatasets, setLockedDatasets] = useState([])
  useEffect(() => {
    if (!canManage) return
    api.trials.getLockedDatasets(trial._id, ({ lockedDatasets }) => setLockedDatasets(lockedDatasets || []), () => setLockedDatasets([]))
  }, [trial._id, trial.isDatabaseLocked, canManage])

  const handleTrialLockChange = (updatedTrial) => {
    if (updatedTrial) {
      updateTrial(trial._id, updatedTrial)
    }
  }

  const handleDatabaseLockChange = (updatedTrial) => {
    if (updatedTrial) {
      updateTrial(trial._id, updatedTrial)
    }
  }

  return (
    <Stack gap={5}>
      <EmptyCard icon='lock' title='Trial and database locking' description='Use locks to prevent changes to the trial configuration or the results database.' />

      <Card variant='outline' gap={2}>
        <Stack direction='row' justifyContent='space-between' alignItems='flex-start'>
          <Box flex={1}>
            <Heading as='h3' size='md' mb={2}>Trial Lock</Heading>
            <Text mb={3}>
              When the trial is locked, stages and datatypes cannot be edited. This prevents changes to the trial configuration and structure.
            </Text>
            <Text fontSize='sm' color='fg.muted' mb={4}>
              Use this lock when you want to prevent modifications to the trial setup while still allowing data entry and other operations.
            </Text>
          </Box>
          {canManage &&
            <Box ml={4}>
              <TrialLocker
                trial={trial}
                isLocking={!trial?.isLocked}
                onComplete={handleTrialLockChange}
              />
            </Box>}
        </Stack>
        <Alert status={trial?.isLocked ? 'info' : 'success'}>
          Status: {trial?.isLocked ? 'Trial is currently locked' : 'Trial is currently unlocked'}
        </Alert>
      </Card>

      <Card variant='outline' gap={2}>
        <Stack direction='row' justifyContent='space-between' alignItems='flex-start'>
          <Box flex={1}>
            <Heading as='h3' size='md' mb={2}>Database Lock</Heading>
            <Text mb={3}>
              When the database is locked, no new data can be created or edited within the trial. This includes responses, participants, queries, and notes.
            </Text>
            <Text fontSize='sm' color='fg.muted' mb={4}>
              Tasks, files, and messages will continue to work normally. Use this lock when you want to prevent data entry while still allowing operational management.
            </Text>
          </Box>
          {canManage &&
            <Box ml={4}>
              <DatabaseLocker
                trial={trial}
                isLocking={!trial?.isDatabaseLocked}
                onComplete={handleDatabaseLockChange}
              />
            </Box>}
        </Stack>
        <Alert status={trial?.isDatabaseLocked ? 'info' : 'success'}>
          Status: {trial?.isDatabaseLocked ? 'Database is currently locked' : 'Database is currently unlocked'}
        </Alert>
      </Card>

      {canManage &&
        <Card variant='outline' gap={2}>
          <Heading as='h3' size='md'>Locked datasets</Heading>
          <Text fontSize='sm' color='fg.muted'>
            Each time the database is locked, Trialflare builds the study record as it stood at that moment.
          </Text>
          {lockedDatasets.length
            ? <SimpleTable
                size='sm'
                headers={['Locked at (UTC)', 'Trialflare', 'Responses', 'Participants', 'Files', '']}
                rows={lockedDatasets.map(d => [
                  utils.formatDateTime(d.generatedAt),
                  <Text key='v' fontFamily='mono' fontSize='xs'>{d.version}</Text>,
                  d.counts?.responses,
                  d.counts?.participants,
                  d.fileCount,
                  <Button key='dl' size='xs' as='a' href={d.request} target='_blank' rel='noopener noreferrer'><Icon as={FaDownload} mr={1} /> Download</Button>
                ])}
              />
            : <Text fontSize='sm' color='fg.subtle'>No locked datasets yet{trial?.isDatabaseLocked ? ' (the record for the current lock is being built)' : ''}.</Text>}
        </Card>}
    </Stack>
  )
}
